import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '@/api/client';
import { Button, ErrorNote, LiveRegion, TextField } from '@/components/ui';
import { AuthLayout, linkClass } from './AuthLayout';

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Enter the email you signed up with.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post('/v1/auth/password/forgot', { body: { email: email.trim() } });
      setSent(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="We’ll email you a link to choose a new one."
      footer={
        <Link to="/sign-in" className={linkClass}>
          Back to sign in
        </Link>
      }
    >
      <LiveRegion message={sent ? `If an account exists for ${email}, a reset link is on its way.` : ''} />
      {sent ? (
        <div className="rounded-card border border-border bg-card p-5 text-sm">
          <p className="font-medium text-ink">Check your inbox</p>
          <p className="mt-1 text-muted-ink">
            If an account exists for <span className="text-ink">{email}</span>, a reset link is on its way.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} noValidate className="flex flex-col gap-4">
          {error && <ErrorNote message={error} />}
          <TextField
            label="Email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
          />
          <Button type="submit" variant="primary" size="lg" loading={busy}>
            Send reset link
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
